import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Bottom banner that prompts the user to install the PWA.
 * Hidden if the app is already installed (display-mode: standalone) or the user dismissed it.
 */
export default function InstallPrompt() {
  const [evt, setEvt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator?.standalone === true;
    if (standalone) return;
    if (sessionStorage.getItem("crickpad-install-dismissed") === "1") return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setEvt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari has no beforeinstallprompt — show manual instructions
    const ua = window.navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(ua);
    const isSafari = /safari/.test(ua) && !/crios|fxios|chrome/.test(ua);
    if (isIos && isSafari) {
      setIosHint(true);
      setVisible(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const install = async () => {
    if (!evt) return;
    evt.prompt();
    const choice = await evt.userChoice;
    if (choice?.outcome === "accepted") {
      setVisible(false);
      sessionStorage.setItem("crickpad-install-dismissed", "1");
    }
  };

  const dismiss = () => {
    sessionStorage.setItem("crickpad-install-dismissed", "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)] bg-card border border-primary p-4 shadow-2xl"
      style={{ borderRadius: 2 }}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-display font-black text-lg shrink-0">
          C
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-black uppercase text-base tracking-tight">
            Install CrickPad
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {iosHint
              ? "Tap Share ↑ then 'Add to Home Screen' to install."
              : "Score on the go — works offline, opens full-screen."}
          </p>
          {!iosHint && (
            <button
              onClick={install}
              data-testid="install-app-btn"
              className="tactical-btn mt-3 bg-primary text-primary-foreground px-4 py-2 text-xs inline-flex items-center gap-1.5"
            >
              <Download className="w-3 h-3" strokeWidth={3} /> Install App
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          data-testid="install-dismiss-btn"
          aria-label="Dismiss"
          className="p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
